<template>
  <q-page
    :class="{
      'q-pa-sm': $q.screen.lt.sm,
      'q-pa-md': !$q.screen.lt.sm
    }"
  >
    <div class="row col-12 items-center">
      <breadcrumbs
        hide-client
        class="col"
        :custom-breadcrumbs="[
          { label: $t('activities.breadcrumbElLabel'), to: { name: 'Activities' } },
          { label: $t('pendingSignatures.breadcrumbElLabel') },
        ]"
      />
      <div class="col-auto q-pb-md">
        <q-btn
          :label="$q.screen.lt.sm ? '' : $t('pendingSignatures.refreshSessionActivities')"
          icon="refresh"
          color="primary"
          flat
          size="sm"
          :loading="isRefreshing"
          @click="onRefreshAll"
        >
          <q-tooltip
            v-if="$q.screen.lt.sm"
          >
            {{ $t('pendingSignatures.refreshSessionActivities') }}
          </q-tooltip>
        </q-btn>
      </div>
    </div>

    <div>
      <q-btn
        :disable="!showSignButton"
        color="primary"
        icon="fas fa-file-signature"
        :label="allRowsSelected ? $t('pendingSignatures.signAllBtn'):
          $t('pendingSignatures.signSomeBtn', {
            selectedRows: selected.length, totalRows: rowCount
          })"
        @click="onShowBulkSignatureDialog"
      >
        <q-tooltip v-if="!showSignButton">
          {{ $t('pendingSignatures.noSignaturesSelected') }}
        </q-tooltip>
      </q-btn>
    </div>

    <session-activities-table
      ref="sessionActivitiesTable"
      v-model:selected="selected"
      v-model:row-count="rowCount"

      :all-programs="allPrograms"
      :default-columns="[
        'status', 'client', 'user', 'startTimestamp', 'endTimestamp', 'sessionName'
      ]"
      :fixed-filters="fixedFilters"
      select-all
      :virtual-scroll="$q.platform.is.desktop"
      :rows-per-page-options="[100]"
      :no-data-label="$t('pendingSignatures.noDataLabel')"

      hide-pagination-label
      disable-export
      disable-send-to-integration
      disable-views
      local-storage-prop="ownActivities"
      bordered
      @click="onViewSession"
    />
  </q-page>
</template>

<script>
import { filterPropertyTypes } from 'boot/globals';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import SessionActivityDialog from 'components/Sessions/Modals/SessionActivityDialog';
import SessionCardDialog from 'components/Sessions/Modals/SessionCardDialog';
import SessionActivitiesTable from 'components/Sessions/SessionActivitiesTable';
import Signature from 'components/Signature';
import { arrayRemove, arrayUnion, documentId } from 'firebase/firestore';
import { pick } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { uid } from 'quasar';
import { sessionActivitiesTableSource, useActivityNavigation } from 'src/composables/useActivityNavigation';
import { useSignature } from 'src/composables/signature';
import MessagesDB from 'src/fb/messages-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionNoteTemplatesDB from 'src/fb/sessionNoteTemplates-db';
import SessionsDB from 'src/fb/sessions-db';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import SessionNotesTemplates from 'src/mixins/SessionNotesTemplates';
import { auth } from 'src/store/auth';
import { clients } from 'src/store/clients';
import { geolocation } from 'src/store/geolocation';
import { programs } from 'src/store/programs';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'PendingSignatures',

  components: { Breadcrumbs, SessionActivitiesTable },

  mixins: [Interactions, SessionActivitiesMixin, SessionNotesTemplates],

  setup() {
    const { getStaffInfoForSignature } = useSignature();
    return {
      getStaffInfoForSignature,
      activityNav: useActivityNavigation(),
    };
  },

  data() {
    return {
      isRefreshing: false,

      rowCount: 0,
      selected: [],

      showSignButton: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'useLegalNameInSignature']),
    ...mapState(programs, ['allPrograms']),

    fixedFilters() {
      return {
        status: {
          id: uid(),
          filterProperty: 'status',
          filterPropertyType: filterPropertyTypes.StringInArray,
          filterStatements: [
            {
              filterCriteriaType: null,
              filterNegative: false,
              filteredItems: [
                'pending-signatures',
              ],
              label: null,
            },
          ],
        },
        requiredSignatoriesUserIds: {
          id: uid(),
          filterProperty: 'requiredSignatoriesUserIds',
          filterPropertyType: filterPropertyTypes.ArrayContainsUser,
          filterStatements: [
            {
              filterCriteriaType: null,
              filterNegative: false,
              filteredItems: [this.userDetails?.userId],
              label: this.userDetails?.name,
            },
          ],
        },
      };
    }, // fixedFilters()

    allRowsSelected() {
      return this.selected.length === this.rowCount;
    },
  },

  watch: {

    selected() {
      this.showSignButton = !!this.selected.length;
    },

  },

  async mounted() {
    this.activityNav.configure({
      source: sessionActivitiesTableSource(() => this.$refs.sessionActivitiesTable),
      openers: {
        activity: (row, controller) => this.openActivityDialog(row, controller),
        session: (row, controller) => this.openSessionDialog(row, controller),
      },
    });

    await this.initializeClients();
    await this.syncClients();
  },

  methods: {
    ...mapActions(geolocation, ['refreshGeolocation', 'executeGeolocationCallback']),
    ...mapActions(clients, ['initializeClients', 'syncClients']),

    async onRefreshAll() {
      this.isRefreshing = true;
      try {
        await this.$refs.sessionActivitiesTable.refreshAll();
      } finally {
        this.isRefreshing = false;
      }
    }, // onRefreshAll()

    onViewSession(sessionId, sessionActivityId, sessionActivity) {
      const row = sessionActivity || { id: sessionActivityId, session: sessionId || null };
      this.activityNav.start(row);
    }, // onViewSession()

    openActivityDialog(row, navController) {
      return this.openComponentDialog(
        SessionActivityDialog,
        { sessionActivityId: row.id, navController },
        async () => {
          if (this?.$refs?.sessionActivitiesTable) {
            this.$refs.sessionActivitiesTable.refresh(navController?.currentId || row.id);
          }
        },
      );
    }, // openActivityDialog()

    async openSessionDialog(row, navController) {
      const sessionsDb = new SessionsDB();
      let session = null;
      let dialogData = null;

      this.$q.loading.show();
      try {
        session = await sessionsDb.read(row.session);
        if (session?.isDeleted) {
          session = null;
        }
        if (session) {
          dialogData = await this.loadSessionDialogData(session);
        }
      } catch (err) {
        this.notifyNegative(err.message);
        return null;
      } finally {
        this.$q.loading.hide();
      }

      if (!session) {
        return this.openActivityDialog(row, navController);
      }

      return this.openComponentDialog(SessionCardDialog, {
        allPrograms: dialogData.allPrograms,
        session,
        client: dialogData.client,
        openSessionActivity: row.id,
        navController,
      }, async () => {
        if (this?.$refs?.sessionActivitiesTable) {
          await this.$refs.sessionActivitiesTable.refreshSession(row.session);
        }
      });
    }, // openSessionDialog()

    onShowBulkSignatureDialog() {
      this.showBulkSignatureDialog();
    }, // onShowBulkSignatureDialog()

    async showBulkSignatureDialog() {
      this.$q.loading.show();

      this.refreshGeolocation(false);

      const sessionNoteTemplateIds = new Set(
        this.selected.map(({ sessionNoteTemplateId }) => sessionNoteTemplateId).filter(Boolean),
      );

      const templateIdsToIncludeCredentialsMap = {};

      if (sessionNoteTemplateIds.size) {
        try {
          const sessionNoteTemplatesDb = new SessionNoteTemplatesDB();
          const sessionNoteTemplates = await sessionNoteTemplatesDb.readAll([
            [documentId(), 'in', [...sessionNoteTemplateIds]],
            ['clinic', '==', this.userDetails.clinic],
          ]);

          sessionNoteTemplates.forEach((template) => {
            templateIdsToIncludeCredentialsMap[template.id] = template.includeProfessionalCredentials || false;
          });
        } catch (err) {
          logEvent(err);
        }
      }

      const { userName, professionalCredentials } = await this.getStaffInfoForSignature(
        this.userDetails.name,
        this.userDetails.userId,
        Object.values(templateIdsToIncludeCredentialsMap).some((includeCredentials) => includeCredentials),
      );

      this.$q.loading.hide();

      this.openComponentDialog(
        Signature,
        {
          userName,
          includeGeolocation: true,
        },
        async (signatureInfo) => {
          const actions = [];
          this.$q.loading.show();

          this.selected.forEach((sessionActivity) => {
            const signatureDetails = {
              userName: signatureInfo.userName,
              userId: this.userDetails.userId,
              label: '',
              ...(signatureInfo.geolocation && { geolocation: signatureInfo.geolocation }),
              ...(templateIdsToIncludeCredentialsMap[sessionActivity.sessionNoteTemplateId]
                && professionalCredentials && {
                professionalCredentials,
              }),
            };

            actions.push(
              this.saveSignature(
                sessionActivity,
                signatureInfo.data,
                signatureDetails,

                this.addSignatureToActivity,
              ),
            );
          });

          try {
            await Promise.all(actions);
            if (this?.$refs?.sessionActivitiesTable) {
              this.$refs.sessionActivitiesTable.refreshAll();
            }
          } catch (err) {
            this.notifyNegative(err.message);
            logEvent(err);
          } finally {
            this.$q.loading.hide();
          }
        },
      );
    }, // showBulkSignatureDialog()

    async addSignatureToActivity(changes, sessionActivity, signature) {
      const resolveRequest = sessionActivity.requiredSignatories?.find(({ userId }) => userId === signature.userId);

      if (!resolveRequest) {
        return;
      }

      const newSignature = {
        ...signature,
        ...pick(resolveRequest, ['clientDataField', 'label', 'clientDataField']),
      };

      const existingRequiredSignatoriesUserIds = sessionActivity.requiredSignatoriesUserIds || [];

      const sessionActivityChanges = {
        id: sessionActivity.id,
        signatures: arrayUnion(newSignature),
        requiredSignatories: arrayRemove(resolveRequest),
        requiredSignatoriesUserIds: existingRequiredSignatoriesUserIds.filter(
          (userId) => userId !== resolveRequest.userId,
        ),
      };

      await Promise.all([
        this.updateSessionActivity(sessionActivity, sessionActivityChanges),
        this.resolveSignatureRequestComments(sessionActivity, resolveRequest),
      ]);
    },

    async updateSessionActivity(activity, sessionActivityChanges) {
      const newStatus = this.getNewSessionActivityStatus(activity, sessionActivityChanges);
      const newChanges = { ...sessionActivityChanges, status: newStatus };

      const sessionActivitiesDb = new SessionActivitiesDB();
      await sessionActivitiesDb.update(newChanges);
    }, // updateSessionActivity()

    async resolveSignatureRequestComments(activity, signatory) {
      if (activity.session === null) {
        return;
      }

      const messagesDb = new MessagesDB();
      const comments = await messagesDb.readAll([
        ['clinic', '==', this.userDetails.clinic],
        ['client', '==', activity.client],
        ['session', '==', activity.session],
        ['type', '==', 'signatureRequest'],
        ['signatoryId', '==', signatory.uid]]);
      return comments.map((comment) => (
        messagesDb.update({ resolved: true, id: comment.id })
      ));
    },

  },
};
</script>
